/*
 * File: TeamRowGrid.tsx
 * Project: a-a
 * File Created: Wednesday, 15th January 2025 8:51:50 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Tuesday, 22nd July 2025 7:28:45 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { Box, Chip, Typography } from '@mui/material';
import zeplinColor from '../../../theme/Color';
import { Link, useNavigate } from 'react-router-dom';
import { LightTooltip } from '../../shared/CustomTooltip/CustomTooltip';
import formatCurrency from '../../../helper/short-currency';

type Team = {
  id: string | null;
  position: string | null;
  name: string | null;
  mobile: string | null;
  phone: string | null;
  address: {
    city: string | null;
    country: string | null;
    geocodeAccuracy: string | null;
    latitude: string | null;
    longitude: string | null;
    postalCode: string | null;
    state: string | null;
    street: string | null;
  } | null;
  company: string | null;
  email: string | null;
  openDeals: string | null;
  openTasks: string | null;
  title: string | null;
};
type TeamData = {
  members: Team[];
};
type TeamWithoutAddress = Omit<Team, 'address'>;

interface Column {
  id: 'name' | 'id' | 'role' | 'openDeals' | 'openTasks';
  label: string;
  minWidth?: number;
  align?: 'center';
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'Name' },
  { id: 'role', label: 'Role', minWidth: 100 },
  {
    id: 'openTasks',
    label: 'Open Tasks',

    align: 'center',
  },
  {
    id: 'openDeals',
    label: 'Open deals',

    align: 'center',
  },
];

const TeamRowGrid = ({
  teams,
  loading,
  category,
}: {
  teams: TeamData;
  loading: boolean;
  category: string | null;
}) => {
  const navigate = useNavigate();

  return (
    <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
      <TableContainer
        sx={{
          // height: window.innerHeight - 250,
          height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 250px)' },
          overflow: 'auto',
        }}
      >
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow
              sx={{
                // backgroundColor: '#f5f5f5', // Table header background color
                border: '1px solid black',
                borderRadius: '14px', // Apply border radius to the table header row
                '& th': {
                  backgroundColor: '#f5f5f5',
                },
                '& th:first-child': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.members.map((row, ind) => {
              return (
                <TableRow
                  hover
                  role='checkbox'
                  tabIndex={-1}
                  key={ind}
                  sx={{
                    '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' }, // Apply bottom border for each row
                  }}
                >
                  {columns.map((column) => {
                    // console.log([column.id as keyof Team]);
                    const value =
                      column.id === 'openDeals'
                        ? `$${formatCurrency(+(row[column.id as keyof TeamWithoutAddress] || 0))}`
                        : row[column.id as keyof TeamWithoutAddress];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        <Link
                          to={row?.id ?? ''}
                          onClick={() =>
                            sessionStorage.setItem('selected-member', JSON.stringify(row))
                          }
                          style={{ textDecoration: 'none', color: zeplinColor.BasicBlack }}
                        >
                          {column.id === 'name' ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                cursor: 'pointer',
                              }}
                              // onClick={() => navigate(row?.AccountId)}
                            >
                              <Box
                                sx={{
                                  width: '60px',
                                  height: '60px',
                                  borderRadius: '14px',
                                  //   border: '1px solid black',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: zeplinColor.Brand1,
                                }}
                              >
                                {/* <img
                                  width={'60px'}
                                  height={'60px'}
                                  src={'/assets/client-demo-img.svg'}
                                  alt='logo'
                                  style={{ objectFit: 'fill' }}
                                /> */}
                                <Typography variant='display5' sx={{ color: zeplinColor.Neutral1 }}>
                                  {value
                                    ?.split(' ')
                                    .map((val, index) =>
                                      index === 0 || index === value?.split(' ').length - 1
                                        ? val[0]
                                        : '',
                                    )}
                                </Typography>
                              </Box>
                              {value ?? ''}
                            </Box>
                          ) : column.id === 'role' ? (
                            <Box sx={{ display: 'flex', gap: '4px' }}>
                              <Chip
                                label={
                                  <Typography
                                    variant='label1'
                                    color={zeplinColor.Neutral1}
                                    sx={{
                                      textAlign: 'center',
                                      display: 'flex',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {row['position'] ?? 'N/A'}
                                  </Typography>
                                }
                                sx={{
                                  backgroundColor: zeplinColor.BasicBlack,
                                  padding: '5px 12px 5px 12px',
                                  borderRadius: '50px',
                                  // width: '47px',
                                  height: '24px',
                                }}
                              />
                              {row['title'] && (
                                <Chip
                                  label={
                                    <Typography
                                      variant='label1'
                                      color={zeplinColor.Neutral1}
                                      sx={{
                                        textAlign: 'center',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                      }}
                                    >
                                      {row['title'] ?? 'N/A'}
                                    </Typography>
                                  }
                                  sx={{
                                    backgroundColor: zeplinColor.BasicBlack,
                                    padding: '5px 12px 5px 12px',
                                    borderRadius: '50px',
                                    // width: '47px',
                                    height: '24px',
                                  }}
                                />
                              )}
                            </Box>
                          ) : column.format && typeof value === 'number' ? (
                            column.format(value)
                          ) : (
                            value
                          )}
                        </Link>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TeamRowGrid;
